import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import ConfirmDialog from '../components/ConfirmDialog';
import SearchableSelect from '../components/SearchableSelect';

// Open window SYNCHRONOUSLY (so popup blocker allows it), then load HTML via API
function openPrintView(invId) {
  const w = window.open('', '_blank');
  if (!w) { toast.error('Popup blocked — allow popups for this site and try again'); return; }
  w.document.write('<html><body style="font-family:sans-serif;padding:40px;color:#555">Loading invoice…</body></html>');
  api.get(`/invoices/${invId}/print`, { responseType: 'text' })
    .then(r => { w.document.open(); w.document.write(r.data); w.document.close(); })
    .catch(() => { w.document.open(); w.document.write('<h3>Failed to load invoice. Please try again.</h3>'); w.document.close(); });
}

const STATUS_COLORS = {
  draft:     { background:'#f0f0f0', color:'#555' },
  sent:      { background:'#E8EEF8', color:'#073E81' },
  paid:      { background:'#e6f9ed', color:'#1a7a3c' },
  partial:   { background:'#FEF3C7', color:'#92400E' },
  approved:  { background:'#EDE9FE', color:'#5B21B6' },
  cancelled: { background:'#FFF0F3', color:'#FF0033' },
};

function fmt(n) {
  return '₹' + parseFloat(n||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function InvoicesPage({ defaultType='invoice' }) {
  const { isOwner, isAccountant } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Summary figures stay hidden until clicked (privacy on shared screens); resets on every visit.
  const [revealed, setRevealed] = useState([]);
  const toggleCard = label => setRevealed(r => r.includes(label) ? r.filter(x => x !== label) : [...r, label]);
  const [statusFilter, setStatusFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [permanentDeleteConfirm, setPermanentDeleteConfirm] = useState(null);
  const [partPaidId, setPartPaidId] = useState(null);
  const partPaidRef = useRef({});
  const [showExport, setShowExport] = useState(false);
  const [exportPeriod, setExportPeriod] = useState('fy');
  const [exportFY, setExportFY] = useState('26/27');
  const [exportMonth, setExportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [exporting, setExporting] = useState(false);

  async function handleExport() {
    setExporting(true);
    try {
      const params = new URLSearchParams({ period: exportPeriod, type: defaultType });
      if (exportPeriod === 'fy') params.set('fy', exportFY);
      else if (exportPeriod === 'month') params.set('month', exportMonth);
      else { params.set('from', exportFrom); params.set('to', exportTo); }
      const res = await api.get(`/invoices/export?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = res.headers['content-disposition']?.match(/filename="(.+)"/)?.[1] || 'Invoices.xlsx';
      a.click();
      URL.revokeObjectURL(url);
      setShowExport(false);
    } catch { toast.error('Export failed'); }
    setExporting(false);
  }

  const isQuotation = defaultType === 'quotation';
  const queryKey = ['invoices', defaultType, statusFilter, clientFilter];

  const { data: invoices=[], isLoading } = useQuery({
    queryKey,
    queryFn: () => api.get('/invoices', { params: { type: defaultType, status: statusFilter||undefined, client_id: clientFilter||undefined } }).then(r => r.data),
  });

  // Unfiltered by status/client — used only to check for a pending deletion of this type, so an
  // active status/client filter can't hide it and let "+ New" appear enabled when it shouldn't be.
  const { data: allOfType=[] } = useQuery({
    queryKey: ['invoices', defaultType, 'pending-delete-check'],
    queryFn: () => api.get('/invoices', { params: { type: defaultType } }).then(r => r.data),
  });

  const { data: clients=[] } = useQuery({ queryKey:['clients'], queryFn:()=>api.get('/clients').then(r=>r.data) });

  const statusMutation = useMutation({
    mutationFn: ({ id, status, paid_amount }) => api.patch(`/invoices/${id}/status`, { status, ...(paid_amount !== undefined && { paid_amount }) }),
    onSuccess: () => { toast.success('Status updated'); qc.invalidateQueries(['invoices']); },
    onError: err => toast.error(err.response?.data?.error || 'Failed to update status'),
  });

  const cancelMutation = useMutation({
    mutationFn: id => api.patch(`/invoices/${id}/cancel`),
    onSuccess: () => { toast.success('Invoice cancelled'); qc.invalidateQueries(['invoices']); setCancelConfirm(null); },
    onError: () => toast.error('Failed to cancel'),
  });

  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/invoices/${id}`),
    onSuccess: () => { toast.success('Moved to trash — you can undo this'); qc.invalidateQueries(['invoices']); setDeleteConfirm(null); },
    onError: err => toast.error(err.response?.data?.error || 'Failed to delete'),
  });

  const undoDeleteMutation = useMutation({
    mutationFn: id => api.post(`/invoices/${id}/undo-delete`),
    onSuccess: () => { toast.success('Restored'); qc.invalidateQueries(['invoices']); },
    onError: err => toast.error(err.response?.data?.error || 'Failed to restore'),
  });

  const permanentDeleteMutation = useMutation({
    mutationFn: id => api.delete(`/invoices/${id}/permanent`),
    onSuccess: () => { toast.success('Permanently deleted'); qc.invalidateQueries(['invoices']); setPermanentDeleteConfirm(null); },
    onError: err => { toast.error(err.response?.data?.error || 'Failed to delete'); setPermanentDeleteConfirm(null); },
  });

  const convertMutation = useMutation({
    mutationFn: id => api.post(`/invoices/${id}/convert`),
    onSuccess: res => {
      toast.success(`Converted → Invoice ${res.data.invoice_number}`);
      qc.invalidateQueries(['invoices']);
      navigate('/invoices');
    },
    onError: err => toast.error(err.response?.data?.error || 'Conversion failed'),
  });

  const duplicateMutation = useMutation({
    mutationFn: id => api.post(`/invoices/${id}/duplicate`),
    onSuccess: res => {
      toast.success(`Duplicated → ${res.data.invoice_number}`);
      qc.invalidateQueries(['invoices']);
      navigate(`/${isQuotation ? 'quotations' : 'invoices'}/${res.data.id}/edit`);
    },
    onError: err => toast.error(err.response?.data?.error || 'Duplicate failed'),
  });

  const totals = {
    count: invoices.length,
    total: invoices.reduce((s, i) => s + parseFloat(i.grand_total||0), 0),
    paid: invoices.filter(i => i.status === 'paid').reduce((s, i) => s + parseFloat(i.grand_total||0), 0),
    pending: invoices.filter(i => ['sent','draft'].includes(i.status)).reduce((s, i) => s + parseFloat(i.grand_total||0), 0),
  };

  // Any deleted_at row of this type blocks creating a new one, to protect the numbering sequence.
  const pendingDeleted = allOfType.find(i => i.deleted_at);

  return (
    <div>
      <div className="section-head">
        <h2>{isQuotation ? 'Quotations' : 'Invoices'}</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-sm" style={{ border: '1px solid var(--border)', background: 'var(--surface)' }}
            onClick={() => setShowExport(v => !v)}>
            ⬇ Export Excel
          </button>
          {isOwner && (
            <button className="btn-primary" disabled={!!pendingDeleted}
              title={pendingDeleted ? `Restore or permanently delete "${pendingDeleted.invoice_number}" first` : ''}
              onClick={() => navigate(isQuotation ? '/quotations/new' : '/invoices/new')}>
              + New {isQuotation ? 'Quotation' : 'Invoice'}
            </button>
          )}
        </div>
      </div>

      {/* Export panel */}
      {showExport && (
        <div className="card" style={{ padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>PERIOD</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {[['fy','Full FY'], ['month','Month'], ['custom','Custom']].map(([v, label]) => (
                <button key={v} className="btn-sm" onClick={() => setExportPeriod(v)}
                  style={{ fontWeight: exportPeriod === v ? 700 : 400, background: exportPeriod === v ? 'var(--accent)' : 'var(--surface)', color: exportPeriod === v ? '#fff' : 'var(--text2)', border: '1px solid var(--border)' }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {exportPeriod === 'fy' && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>FINANCIAL YEAR</div>
              <select className="form-input" style={{ width: 120, padding: '5px 8px' }} value={exportFY} onChange={e => setExportFY(e.target.value)}>
                {['23/24','24/25','25/26','26/27','27/28'].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          )}
          {exportPeriod === 'month' && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>MONTH</div>
              <input type="month" className="form-input" style={{ padding: '5px 8px' }} value={exportMonth} onChange={e => setExportMonth(e.target.value)} />
            </div>
          )}
          {exportPeriod === 'custom' && (
            <>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>FROM</div>
                <input type="date" className="form-input" style={{ padding: '5px 8px' }} value={exportFrom} onChange={e => setExportFrom(e.target.value)} />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>TO</div>
                <input type="date" className="form-input" style={{ padding: '5px 8px' }} value={exportTo} onChange={e => setExportTo(e.target.value)} />
              </div>
            </>
          )}

          <button className="btn-primary" onClick={handleExport} disabled={exporting}
            style={{ whiteSpace: 'nowrap' }}>
            {exporting ? 'Exporting…' : '⬇ Download .xlsx'}
          </button>
          <button className="btn-ghost btn-sm" onClick={() => setShowExport(false)}>Cancel</button>
        </div>
      )}

      {/* Summary cards */}
      {!isQuotation && (
        <>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
          <button type="button" onClick={() => setRevealed(revealed.length === 4 ? [] : ['Total Invoiced','Paid','Pending','Total Invoices'])}
            style={{ background: 'none', border: 'none', fontSize: 12, color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }}>
            {revealed.length === 4 ? '🙈 Hide all' : '👁 Show all'}
          </button>
        </div>
        <div className="form-grid" style={{ marginBottom: 16 }}>
          {[
            { label: 'Total Invoiced', val: fmt(totals.total), color: 'var(--accent)' },
            { label: 'Paid', val: fmt(totals.paid), color: '#1a7a3c' },
            { label: 'Pending', val: fmt(totals.pending), color: '#b45309' },
            { label: 'Total Invoices', val: totals.count, color: 'var(--text2)' },
          ].map(c => {
            const shown = revealed.includes(c.label);
            return (
              <div key={c.label} className="card" onClick={() => toggleCard(c.label)}
                title={shown ? 'Click to hide' : 'Click to show'}
                style={{ padding: '14px 16px', cursor: 'pointer', userSelect: 'none' }}>
                <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                  <span>{c.label}</span><span style={{ opacity: .6 }}>{shown ? '🙈' : '👁'}</span>
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: shown ? c.color : 'var(--text3)', letterSpacing: shown ? 0 : 2 }}>
                  {shown ? c.val : '••••••'}
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        {(isQuotation ? ['','draft','sent','approved','cancelled'] : ['','draft','sent','paid','cancelled']).map(s => (
          <button key={s} className="btn-sm" onClick={() => setStatusFilter(s)}
            style={{ background: statusFilter === s ? 'var(--accent)' : '', color: statusFilter === s ? '#fff' : '', fontWeight: statusFilter === s ? 600 : 400 }}>
            {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <div style={{ width: 180, marginLeft: 8 }}>
          <SearchableSelect value={clientFilter} onChange={setClientFilter}
            items={clients} allowClear clearLabel="All clients" placeholder="All clients" />
        </div>
        {clientFilter && (
          <button className="btn-ghost btn-sm" onClick={() => setClientFilter('')}>✕ Clear client</button>
        )}
      </div>

      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th>{isQuotation ? 'Quotation No.' : 'Invoice No.'}</th>
              <th>Client</th>
              <th>Date</th>
              {isQuotation ? <th>Valid Until</th> : <th>Due</th>}
              <th style={{ textAlign: 'right' }}>Taxable</th>
              {!isQuotation && <>
                <th style={{ textAlign: 'right', color: '#1E7E4E' }}>SGST</th>
                <th style={{ textAlign: 'right', color: '#1E7E4E' }}>CGST</th>
                <th style={{ textAlign: 'right', color: '#1E7E4E' }}>IGST</th>
              </>}
              <th style={{ textAlign: 'right' }}>Total</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? <tr><td colSpan={11}><div className="empty"><div className="spinner"/></div></td></tr>
              : invoices.length === 0
              ? <tr><td colSpan={11}><div className="empty">No {isQuotation ? 'quotations' : 'invoices'} yet</div></td></tr>
              : invoices.map(inv => {
                  const sc = STATUS_COLORS[inv.status] || STATUS_COLORS.draft;
                  const isPartPaidOpen = partPaidId === inv.id;
                  const balance = parseFloat(inv.grand_total||0) - parseFloat(inv.paid_amount||0);
                  const canPay = isOwner || isAccountant;
                  const isDeleted = !!inv.deleted_at;
                  const rowClass = inv.status === 'paid' ? 'row-paid' : inv.status === 'partial' ? 'row-partial' : undefined;
                  return (
                    <React.Fragment key={inv.id}>
                      <tr className={rowClass} style={isDeleted ? { opacity: 0.45, filter: 'grayscale(0.6)' } : undefined}>
                        <td style={{ fontWeight: 600, fontFamily: 'monospace', fontSize: 12 }}>{inv.invoice_number}</td>
                        <td>{inv.client_name}</td>
                        <td style={{ color: 'var(--text2)', fontSize: 11 }}>{new Date(inv.issued_at).toLocaleDateString('en-IN')}</td>
                        <td style={{ color: 'var(--text2)', fontSize: 11 }}>
                          {isQuotation
                            ? (inv.valid_until ? new Date(inv.valid_until).toLocaleDateString('en-IN') : '—')
                            : (inv.due_at ? new Date(inv.due_at).toLocaleDateString('en-IN') : '—')}
                        </td>
                        <td style={{ textAlign: 'right', fontSize: 11 }}>{fmt(inv.subtotal)}</td>
                        {!isQuotation && <>
                          <td style={{ textAlign: 'right', fontSize: 11, color: '#1E7E4E' }}>{parseFloat(inv.sgst_amount||0) > 0 ? fmt(inv.sgst_amount) : '—'}</td>
                          <td style={{ textAlign: 'right', fontSize: 11, color: '#1E7E4E' }}>{parseFloat(inv.cgst_amount||0) > 0 ? fmt(inv.cgst_amount) : '—'}</td>
                          <td style={{ textAlign: 'right', fontSize: 11, color: '#1E7E4E' }}>{parseFloat(inv.igst_amount||0) > 0 ? fmt(inv.igst_amount) : '—'}</td>
                        </>}
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>
                          {fmt(inv.grand_total)}
                          {inv.status === 'partial' && (
                            <div style={{ fontSize: 10, color: '#D97706' }}>Paid {fmt(inv.paid_amount)} · Bal {fmt(balance)}</div>
                          )}
                        </td>
                        <td>
                          <span style={{ ...sc, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                            {inv.status === 'partial' ? 'part paid' : inv.status}
                          </span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                          {isDeleted ? (
                            <>
                              <span style={{ marginRight: 8, fontSize: 11, fontWeight: 600, color: 'var(--red)' }}>Deleted — pending</span>
                              {isOwner && (
                                <>
                                  <button className="btn-sm" style={{ marginRight: 4 }} onClick={() => undoDeleteMutation.mutate(inv.id)} disabled={undoDeleteMutation.isPending}>↩ Undo delete</button>
                                  <button className="btn-danger btn-sm" onClick={() => setPermanentDeleteConfirm(inv.id)}>Delete permanently</button>
                                </>
                              )}
                            </>
                          ) : (
                          <>
                          <button className="btn-sm" style={{ marginRight: 4 }} onClick={() => openPrintView(inv.id)}>🖨 Print / PDF</button>
                          {isOwner && (
                            <button className="btn-sm" style={{ marginRight: 4 }} disabled={duplicateMutation.isPending || !!pendingDeleted}
                              title={pendingDeleted ? `Restore or permanently delete "${pendingDeleted.invoice_number}" first` : ''}
                              onClick={() => duplicateMutation.mutate(inv.id)}>⧉ Duplicate</button>
                          )}
                          {isOwner && inv.status === 'draft' && (
                            <button className="btn-sm" style={{ marginRight: 4 }} onClick={() => statusMutation.mutate({ id: inv.id, status: 'sent' })}>Mark Sent</button>
                          )}
                          {isOwner && inv.status === 'sent' && (
                            <button className="btn-sm" style={{ marginRight: 4, color:'var(--text2)' }}
                              onClick={() => { if(window.confirm('Revert to Draft so it can be edited?')) statusMutation.mutate({ id: inv.id, status: 'draft' }); }}>
                              Unmark
                            </button>
                          )}
                          {/* Fully paid badge (status indicator, not a button) — invoices only */}
                          {!isQuotation && inv.status === 'paid' && (
                            <span style={{ marginRight: 4, display:'inline-flex', alignItems:'center', gap:4, background:'#D1FAE5', color:'#065F46', fontWeight:700, fontSize:11, padding:'3px 10px', borderRadius:4 }}>
                              ✓ Fully Paid
                            </span>
                          )}
                          {/* Payment action buttons for sent or partial — invoices only, quotations aren't "paid" */}
                          {!isQuotation && canPay && (inv.status === 'sent' || inv.status === 'partial') && (
                            <>
                              <button className="btn-sm" style={{ marginRight: 4, border:'1.5px solid #065F46', color:'#065F46', fontWeight:600, background:'transparent' }}
                                onClick={() => statusMutation.mutate({ id: inv.id, status: 'paid' })}>
                                Mark Fully Paid
                              </button>
                              <button className="btn-sm" style={{ marginRight: 4, background: isPartPaidOpen ? '#F59E0B' : '#FEF3C7', color:'#92400E', fontWeight:600, transition:'background 0.2s' }}
                                onClick={() => setPartPaidId(isPartPaidOpen ? null : inv.id)}>
                                ½ Part Paid
                              </button>
                            </>
                          )}
                          {/* Unmark payment — invoices only */}
                          {!isQuotation && canPay && (inv.status === 'paid' || inv.status === 'partial') && (
                            <button className="btn-sm" style={{ marginRight: 4, color:'var(--text2)' }}
                              onClick={() => { if(window.confirm('Revert payment status to Sent?')) statusMutation.mutate({ id: inv.id, status: 'sent' }); }}>
                              Unmark
                            </button>
                          )}
                          {/* Approved badge / action — quotations only */}
                          {isQuotation && inv.status === 'approved' && (
                            <span style={{ marginRight: 4, display:'inline-flex', alignItems:'center', gap:4, background:'#EDE9FE', color:'#5B21B6', fontWeight:700, fontSize:11, padding:'3px 10px', borderRadius:4 }}>
                              ✓ Approved
                            </span>
                          )}
                          {isQuotation && isOwner && inv.status === 'sent' && (
                            <button className="btn-sm" style={{ marginRight: 4, border:'1.5px solid #5B21B6', color:'#5B21B6', fontWeight:600, background:'transparent' }}
                              onClick={() => statusMutation.mutate({ id: inv.id, status: 'approved' })}>
                              Mark Approved
                            </button>
                          )}
                          {isQuotation && isOwner && inv.status === 'approved' && (
                            <button className="btn-sm" style={{ marginRight: 4, color:'var(--text2)' }}
                              onClick={() => { if(window.confirm('Revert to Sent?')) statusMutation.mutate({ id: inv.id, status: 'sent' }); }}>
                              Unmark
                            </button>
                          )}
                          {isOwner && isQuotation && inv.status !== 'cancelled' && (
                            <button className="btn-sm" style={{ marginRight: 4 }} onClick={() => convertMutation.mutate(inv.id)} disabled={convertMutation.isPending}>→ Invoice</button>
                          )}
                          {isOwner && inv.status === 'draft' && (
                            <button className="btn-sm" style={{ marginRight: 4 }} onClick={() => navigate(`/${isQuotation ? 'quotations' : 'invoices'}/${inv.id}/edit`)}>Edit</button>
                          )}
                          {isOwner && inv.status !== 'cancelled' && (
                            <button className="btn-danger btn-sm" style={{ marginRight: 4 }} onClick={() => setCancelConfirm(inv.id)}>Cancel</button>
                          )}
                          {isOwner && (
                            <button className="btn-danger btn-sm" onClick={() => setDeleteConfirm(inv.id)}>🗑</button>
                          )}
                          </>
                          )}
                        </td>
                      </tr>
                      {/* Part paid input row */}
                      <tr style={{ display: isPartPaidOpen ? '' : 'none' }}>
                        <td colSpan={11} style={{ background:'#FFFBEB', padding: 0, borderTop: 'none' }}>
                          <div style={{ overflow: 'hidden', maxHeight: isPartPaidOpen ? 80 : 0, transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1)', padding: isPartPaidOpen ? '10px 16px' : '0 16px' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:10, fontSize:13 }}>
                              <span style={{ color:'var(--text2)' }}>Amount received:</span>
                              <input type="number" min="1" max={inv.grand_total} step="0.01"
                                defaultValue={parseFloat(inv.paid_amount||0) || ''}
                                ref={el => { if(el) partPaidRef.current[inv.id] = el; }}
                                style={{ width:140, padding:'4px 8px', borderRadius:4, border:'1px solid var(--border)', fontSize:13 }}
                                placeholder={`Max ${fmt(inv.grand_total)}`}
                              />
                              <button className="btn-primary btn-sm" onClick={() => {
                                const amt = parseFloat(partPaidRef.current[inv.id]?.value);
                                if (!amt || amt <= 0) return toast.error('Enter a valid amount');
                                if (amt >= parseFloat(inv.grand_total)) return toast.error('Use "Fully Paid" for full payment');
                                statusMutation.mutate({ id: inv.id, status: 'partial', paid_amount: amt });
                                setPartPaidId(null);
                              }}>Save</button>
                              <button className="btn-ghost btn-sm" onClick={() => setPartPaidId(null)}>Cancel</button>
                              <span style={{ fontSize:11, color:'var(--text2)' }}>Balance will be {fmt(parseFloat(inv.grand_total) - (parseFloat(partPaidRef.current[inv.id]?.value)||0))}</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })
            }
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={!!cancelConfirm}
        title="Cancel this document?"
        message="The invoice/quotation will be marked as cancelled. It will still appear in your records."
        confirmLabel="Cancel it"
        onConfirm={() => cancelMutation.mutate(cancelConfirm)}
        onCancel={() => setCancelConfirm(null)}
      />
      <ConfirmDialog
        open={!!deleteConfirm}
        title="Delete this document?"
        message="It will be moved out of the way and can be restored, or permanently deleted later. While it's pending, you won't be able to create a new invoice/quotation of the same type."
        confirmLabel="Delete"
        onConfirm={() => deleteMutation.mutate(deleteConfirm)}
        onCancel={() => setDeleteConfirm(null)}
      />
      <ConfirmDialog
        open={!!permanentDeleteConfirm}
        title="Delete permanently?"
        message="This will permanently delete the document and all its line items. This cannot be undone."
        confirmLabel="Delete permanently"
        onConfirm={() => permanentDeleteMutation.mutate(permanentDeleteConfirm)}
        onCancel={() => setPermanentDeleteConfirm(null)}
      />
    </div>
  );
}
